import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useCedulasContext } from 'Context/CedulasPageContext';
import { urlToFile, toBase64 } from 'Utils/helpers';
import {
    formatLocation,
    CONTACT_INFO,
    processSenasParticulares,
    processVestimenta,
} from 'Utils/MissingPersonUtils';
import { MARCA_AGUA, LOGO_COBUPEJ_CEDULA, LOGO_JAL_CEDULA, X_ICON, FB_ICON } from 'Assets/index.js';
import { Card, Typography, QRCode, message, Skeleton } from 'antd';
import { PNG as styles, PNG_L, pngBoxClip, pngBoxFont, titularFor } from './styles';
import { DISCLAIMER } from './pngCapture';

const PngDataRow = ({ label, value }) => (
    <div style={styles.dataRow}>
        <Typography style={styles.dataLabel}>{label}:&nbsp;</Typography>
        <Typography style={styles.dataValue} title={String(value)}>{value}</Typography>
    </div>
);

const useBoxFont = (text, space) => {
    const ref = useRef(null);
    const [fitted, setFitted] = useState(null);
    const estimate = pngBoxFont(text, space);
    const base = parseFloat(estimate.fontSize);
    const font = fitted?.text === text && fitted?.space === space ? fitted.font : estimate;
    useLayoutEffect(() => {
        const el = ref.current;
        let cancelled = false;
        const min = Math.max(6, base * 95 / 100);
        const fit = () => {
            if (cancelled || !el) return;
            let fittedFont;
            for (let px = base; ; px = Math.max(min, px - 0.25)) {
                fittedFont = { fontSize: `${px}px`, lineHeight: `${px + 2}px` };
                Object.assign(el.style, fittedFont, pngBoxClip(space, px + 2));
                if (el.scrollHeight <= el.clientHeight || px === min) break;
            }
            setFitted({ text, space, font: fittedFont });
        };
        fit();
        // La fuente definitiva puede cambiar los saltos de línea aunque el texto no cambie.
        document.fonts?.ready.then(fit);
        return () => { cancelled = true; };
    }, [base, space, text]);
    return [ref, { ...font, ...pngBoxClip(space, font.lineHeight) }];
};

const MissingPersonPng = ({ missingPersonData, handleDownloadStatus, viewScale }) => {
    const {
        id_cedula_busqueda, ruta_foto, nombre_completo,
        edad_momento_desaparicion, colonia, municipio, estado,
        fecha_desaparicion, sexo, complexion, tez, ojos_color, genero, estatura,
        cabello, descripcion_vestimenta, descripcion_sena_particular,
        condicion_localizacion
    } = missingPersonData;

    const { hasInfoField, verifyAge, verifyStature, formatDate } = useCedulasContext();
    const [image, setImage] = useState(null);

    const titular = titularFor(condicion_localizacion);

    const vestimenta = processVestimenta(descripcion_vestimenta);
    const senas = processSenasParticulares(descripcion_sena_particular);
    const ubicacion = formatLocation(colonia, municipio, estado);

    const vestimentaText = vestimenta.length
        ? vestimenta.map(item => item.formattedText).join('. ')
        : 'SIN DATOS';
    const senasText = senas.length
        ? senas.map(item => item.formattedText).join('. ')
        : 'SIN DATOS';

    const [senasRef, senasFont] = useBoxFont(senasText, PNG_L.senasSpace);
    const [vestimentaRef, vestimentaFont] = useBoxFont(vestimentaText, PNG_L.vestimentaSpace);

    useEffect(() => {
        let cancelled = false;
        setImage(null);

        const getImage = async () => {
            try {
                const fileImage = await urlToFile(ruta_foto);
                const nextImage = fileImage ? await toBase64(fileImage) : "/no_image.png";
                if (!cancelled) setImage(nextImage);
            } catch (error) {
                if (!cancelled) {
                    setImage("/no_image.png");
                    message.error(`Error al convertir la imagen: ${error?.message ?? error}`);
                }
            }
        };
        getImage();

        return () => { cancelled = true; };
    }, [ruta_foto]);

    return (
        <Card
            style={{
                ...styles.cardWrapper,
                ...(viewScale && { transform: `scale(${viewScale})`, transformOrigin: '0 0' })
            }}
            styles={{ body: styles.cardBody }}
        >
            <img
                src={MARCA_AGUA}
                style={styles.watermark}
                alt={'Marca de agua imagen'}
            />

            <div style={styles.row}>
                <div style={styles.colLeft}>
                    <div style={styles.photoBox}>
                        {image
                            ? <img
                                src={image}
                                style={styles.personImage}
                                alt={'Imagen de persona desaparecida'}
                                onLoad={() => handleDownloadStatus && handleDownloadStatus({ isLoadingPng: true })}
                                onError={() => {
                                    if (image !== "/no_image.png") {
                                        setImage("/no_image.png");
                                    } else {
                                        message.error('No se pudo cargar la imagen de la persona.');
                                        handleDownloadStatus?.({
                                            isErrorImage: true,
                                            isLoadingImage: false,
                                            isLoadingPng: false,
                                            openModal: false,
                                        });
                                    }
                                }}
                            />
                            : <Skeleton.Image active block style={styles.skeletonImage} />
                        }
                    </div>

                    <div style={styles.contactBox}>
                        <Typography style={styles.contactTitle}>
                            ¿Tienes información sobre su paradero?
                        </Typography>
                        <Typography style={styles.contactSubtitle}>
                            Comunícate con nosotros:
                        </Typography>
                        <Typography style={styles.phoneText}>
                            {CONTACT_INFO.PHONE}
                        </Typography>
                        <Typography style={styles.emailText}>
                            {CONTACT_INFO.EMAIL}
                        </Typography>
                        <div style={styles.qrWrapper}>
                            <QRCode
                                type="svg"
                                bordered={false}
                                size={PNG_L.qr}
                                value={`${window.location.protocol}//${window.location.host}/cedulas-de-busqueda?id_cedula_busqueda=${id_cedula_busqueda}&qr=true`}
                                style={styles.qrCode}
                            />
                        </div>
                        <div style={styles.redesRow}>
                            <img src={FB_ICON} alt={'Facebook'} style={styles.redesIcon} />
                            <img src={X_ICON} alt={'X'} style={styles.redesIcon} />
                            <Typography className="cedula-png-social-handle" style={styles.redesText}>
                                {CONTACT_INFO.SOCIAL_MEDIA}
                            </Typography>
                        </div>
                    </div>

                    <div style={styles.logoRow}>
                        <img src={LOGO_COBUPEJ_CEDULA} alt={'Comisión Busqueda Logo'} style={styles.cobupejLogo} />
                        <img src={LOGO_JAL_CEDULA} alt={'Gobierno de Jalisco'} style={styles.logoJal} />
                    </div>
                </div>

                <div style={styles.colRight}>
                    <Typography style={{ ...styles.title, color: titular.color }}>
                        {titular.text}
                    </Typography>

                    <Typography style={styles.name}>
                        {hasInfoField(nombre_completo)}
                    </Typography>

                    <div style={styles.lastSeen}>
                        <Typography style={styles.lastSeenLabel}>Se le vio por última vez el:</Typography>
                        <Typography style={styles.lastSeenDate}>{formatDate(fecha_desaparicion)}</Typography>
                        <Typography style={styles.lastSeenPlace}>
                            <span style={styles.lastSeenLabel}>En:&nbsp;</span>{ubicacion}
                        </Typography>
                    </div>

                    <div style={styles.dataList}>
                        <PngDataRow
                            label="Edad al momento de la desaparición"
                            value={verifyAge(edad_momento_desaparicion)}
                        />
                        <PngDataRow label="Sexo" value={hasInfoField(sexo)} />
                        <PngDataRow label="Género" value={hasInfoField(genero)} />
                    </div>

                    <div style={styles.boxFisicas}>
                        <Typography style={styles.boxLabel}>Características físicas:</Typography>
                        <PngDataRow label="Estatura" value={verifyStature(estatura)} />
                        <PngDataRow label="Complexión" value={hasInfoField(complexion)} />
                        <PngDataRow label="Tez" value={hasInfoField(tez)} />
                        <PngDataRow label="Color de ojos" value={hasInfoField(ojos_color)} />
                        <PngDataRow label="Cabello" value={hasInfoField(cabello)} />
                        <div style={styles.boxSpacer} />
                        <Typography style={styles.boxLabel}>Señas particulares:</Typography>
                        <div style={styles.boxGap} />
                        <Typography ref={senasRef} style={{ ...styles.boxText, ...senasFont }}>
                            {senasText}
                        </Typography>
                    </div>

                    <div style={styles.boxVestimenta}>
                        <Typography style={styles.boxLabel}>Vestimenta:</Typography>
                        <div style={styles.boxGap} />
                        <Typography ref={vestimentaRef} style={{ ...styles.boxText, ...vestimentaFont }}>
                            {vestimentaText}
                        </Typography>
                    </div>

                    <Typography style={styles.disclaimer}>{DISCLAIMER}</Typography>
                </div>
            </div>
        </Card>
    );
};

export default MissingPersonPng;
